import React, { useEffect, useState } from 'react';
import { useRive } from '@rive-app/react-canvas';
import AnimatedAvatar from './AnimatedAvatar';

/**
 * RiveAvatar
 * - Soporta dos modos:
 *   1. State machine con inputs booleanos (props `inputs`).
 *   2. Animaciones nombradas que se reproducen según el estado (props `animations`).
 * - Si el .riv no carga, hace fallback al SVG AnimatedAvatar.
 */
const RiveAvatar = ({
    isSpeaking = false,
    isListening = false,
    isThinking = false,
    src = '/animated-head.riv',
    animations = {
        idle: 'idle',
        speaking: 'mouthHappy',
        thinking: 'mouthClosed',
        listening: null,
    },
}) => {
    const [loadError, setLoadError] = useState(false);

    const { rive, RiveComponent } = useRive({
        src,
        autoplay: false,
        onLoadError: () => setLoadError(true),
    });

    useEffect(() => {
        if (!rive) return;
        try {
            const animNames = rive.animationNames || [];
            console.log('[RiveAvatar] animaciones disponibles:', animNames);
            // Siempre arrancar idle como base + boca cerrada por defecto para
            // evitar la sonrisa estática que el .riv trae como pose inicial.
            if (animations.idle) rive.play(animations.idle);
            if (animations.thinking) rive.play(animations.thinking);
        } catch (e) { console.warn('[RiveAvatar]', e); }
    }, [rive, animations.idle, animations.thinking]);

    // Reproducir animación de estado en capa
    useEffect(() => {
        if (!rive) return;
        const stateAnims = [animations.speaking, animations.thinking, animations.listening].filter(Boolean);

        // Detener todas las anims de estado
        stateAnims.forEach((name) => {
            try { rive.stop(name); } catch (e) { /* ignore */ }
        });

        let current = null;
        if (isSpeaking && animations.speaking) current = animations.speaking;
        else if (isThinking && animations.thinking) current = animations.thinking;
        else if (isListening && animations.listening) current = animations.listening;

        if (current) {
            try { rive.play(current); } catch (e) { console.warn('[RiveAvatar] play error', current, e); }
        } else if (animations.thinking) {
            // En reposo, forzamos boca cerrada para evitar la sonrisa estática
            // que el .riv trae como pose por defecto.
            try { rive.play(animations.thinking); } catch (e) { /* ignore */ }
        }
    }, [rive, isSpeaking, isThinking, isListening, animations.speaking, animations.thinking, animations.listening]);

    // Lipsync simulado: alternar boca abierta/cerrada mientras habla
    useEffect(() => {
        if (!rive || !isSpeaking) return;
        const open = animations.speaking;
        const closed = animations.thinking; // mouthClosed
        if (!open || !closed) return;

        let isOpen = true;
        const interval = setInterval(() => {
            try {
                rive.stop(isOpen ? closed : open);
                rive.play(isOpen ? open : closed);
                isOpen = !isOpen;
            } catch (e) { /* ignore */ }
        }, 140 + Math.random() * 80);
        return () => clearInterval(interval);
    }, [rive, isSpeaking, animations.speaking, animations.thinking]);

    if (loadError) {
        return <AnimatedAvatar isSpeaking={isSpeaking} isListening={isListening} isThinking={isThinking} />;
    }

    const ringColor = isSpeaking ? '#1A4731' : '#2D7A4F';

    return (
        <div className="relative flex items-center justify-center select-none" style={{ width: 176, height: 176 }}>
            {(isSpeaking || isListening) && (
                <>
                    <div className="absolute rounded-full animate-ping" style={{
                        inset: -6, background: `${ringColor}1A`, animationDuration: '1.8s'
                    }} />
                    <div className="absolute rounded-full animate-ping" style={{
                        inset: -16, background: `${ringColor}0D`, animationDuration: '2.4s', animationDelay: '0.5s'
                    }} />
                </>
            )}

            <div className="absolute inset-0 rounded-full overflow-hidden" style={{ background: '#C8E6C9' }}>
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        WebkitMaskImage: 'radial-gradient(ellipse 48% 56% at 50% 46%, black 98%, transparent 100%)',
                        maskImage: 'radial-gradient(ellipse 48% 56% at 50% 46%, black 98%, transparent 100%)',
                    }}
                >
                    <RiveComponent style={{ width: '100%', height: '100%' }} />
                </div>
            </div>

            <div
                className="absolute rounded-full transition-all duration-300"
                style={{
                    width: 26, height: 26,
                    bottom: 2, right: 2,
                    zIndex: 2,
                    background: '#DCE3E5',
                    padding: 4,
                }}
            >
                <div
                    className="rounded-full w-full h-full"
                    style={{
                        background: isThinking ? '#F5B848' : '#5DD39E',
                    }}
                />
            </div>
        </div>
    );
};

export default RiveAvatar;
